import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { billingApi, isNexusBuilderSession, NEXUS_AUTH_EVENT } from '../api/billingApi';

/**
 * NexusBillingContext — the single client-side view of the builder's
 * feature-billing state: usage summary (free/paid balances per feature),
 * the purchase catalog (products + current prices), the cart, and the
 * "you've run out" modal that any feature can open with the backend's
 * structured 402 payload.
 *
 * All numbers come from the backend; this context only caches and refreshes
 * them. It is a no-op for non-Nexus sessions (the reused learner components
 * also render in the learner app / tenant portals where it isn't mounted).
 */
const NexusBillingContext = createContext(null);

export const NexusBillingProvider = ({ children }) => {
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quotaModal, setQuotaModal] = useState(null);       // structured 402 payload or null
  const [authRequired, setAuthRequired] = useState(false);  // session token missing/expired on a money route
  const inflight = useRef(null);

  const enabled = isNexusBuilderSession();

  const refreshSummary = useCallback(async () => {
    if (!enabled) return null;
    try {
      const s = await billingApi.getSummary();
      setSummary(s);
      return s;
    } catch (e) {
      setError(e);
      return null;
    }
  }, [enabled]);

  const refreshCatalog = useCallback(async () => {
    if (!enabled) return null;
    try {
      const c = await billingApi.getCatalog();
      setCatalog(c);
      return c;
    } catch (e) {
      setError(e);
      return null;
    }
  }, [enabled]);

  const refreshCart = useCallback(async () => {
    if (!enabled) return null;
    try {
      const c = await billingApi.getCart();
      setCart(c);
      return c;
    } catch (e) {
      // cart needs the session token; a missing token is surfaced by the auth event
      if (e.status !== 401) setError(e);
      return null;
    }
  }, [enabled]);

  const refreshAll = useCallback(async () => {
    if (!enabled) return;
    if (inflight.current) return inflight.current;
    setLoading(true);
    inflight.current = Promise.all([refreshSummary(), refreshCatalog(), refreshCart()]).finally(() => {
      setLoading(false);
      inflight.current = null;
    });
    return inflight.current;
  }, [enabled, refreshSummary, refreshCatalog, refreshCart]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    const onAuth = () => setAuthRequired(true);
    window.addEventListener(NEXUS_AUTH_EVENT, onAuth);
    return () => window.removeEventListener(NEXUS_AUTH_EVENT, onAuth);
  }, []);

  const addToCart = useCallback(async (args) => {
    const c = await billingApi.addToCart(args);
    setCart(c);
    return c;
  }, []);
  const updateCartItem = useCallback(async (id, quantity) => { const c = await billingApi.updateCartItem(id, quantity); setCart(c); return c; }, []);
  const removeCartItem = useCallback(async (id) => { const c = await billingApi.removeCartItem(id); setCart(c); return c; }, []);
  const clearCart = useCallback(async () => { const c = await billingApi.clearCart(); setCart(c); return c; }, []);

  /** Open the exhaustion modal with a backend 402 payload (feature/technology aware). */
  const openQuotaModal = useCallback((payload) => { if (payload) setQuotaModal(payload); }, []);
  const closeQuotaModal = useCallback(() => setQuotaModal(null), []);

  const cartCount = cart ? cart.itemCount : (summary ? summary.cartCount : 0) || 0;

  const value = useMemo(() => ({
    enabled, loading, error,
    summary, catalog, cart, cartCount,
    features: summary ? summary.features : null,
    refreshSummary, refreshCatalog, refreshCart, refreshAll,
    addToCart, updateCartItem, removeCartItem, clearCart,
    quotaModal, openQuotaModal, closeQuotaModal,
    authRequired, clearAuthRequired: () => setAuthRequired(false),
  }), [enabled, loading, error, summary, catalog, cart, cartCount, refreshSummary, refreshCatalog, refreshCart, refreshAll, addToCart, updateCartItem, removeCartItem, clearCart, quotaModal, openQuotaModal, closeQuotaModal, authRequired]);

  return <NexusBillingContext.Provider value={value}>{children}</NexusBillingContext.Provider>;
};

const NOOP = {
  enabled: false, loading: false, error: null, summary: null, catalog: null, cart: null, cartCount: 0, features: null,
  refreshSummary: async () => null, refreshCatalog: async () => null, refreshCart: async () => null, refreshAll: async () => null,
  addToCart: async () => null, updateCartItem: async () => null, removeCartItem: async () => null, clearCart: async () => null,
  quotaModal: null, openQuotaModal: () => {}, closeQuotaModal: () => {}, authRequired: false, clearAuthRequired: () => {},
};

/** Safe everywhere: returns a no-op object when the provider isn't mounted (learner app / tenant portal). */
export const useNexusBilling = () => useContext(NexusBillingContext) || NOOP;

export default NexusBillingContext;
